import type {
  AnalyticsSdkEvent,
  EnqueuePosition,
  EventQueue,
} from './analytics-sdk.types';
import {
  DEFAULT_MAX_QUEUE_SIZE,
  DEFAULT_PERSISTENCE_DEBOUNCE_MS,
  EVENT_QUEUE_STORAGE_KEY,
} from './analytics-sdk.types';

const readFromStorage = (): AnalyticsSdkEvent[] => {
  try {
    const raw = localStorage.getItem(EVENT_QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeToStorage = (events: AnalyticsSdkEvent[]): void => {
  try {
    localStorage.setItem(EVENT_QUEUE_STORAGE_KEY, JSON.stringify(events));
  } catch {
    // Silently ignore — localStorage may be unavailable or full.
  }
};

const clearStorage = (): void => {
  try {
    localStorage.removeItem(EVENT_QUEUE_STORAGE_KEY);
  } catch {
    // Silently ignore.
  }
};

export const createEventQueue = (
  maxSize = DEFAULT_MAX_QUEUE_SIZE,
  debounceMs = DEFAULT_PERSISTENCE_DEBOUNCE_MS,
): EventQueue => {
  const events: AnalyticsSdkEvent[] = readFromStorage();
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const schedulePersist = (): void => {
    if (debounceTimer !== null) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      if (events.length > 0) {
        writeToStorage(events);
      } else {
        clearStorage();
      }
    }, debounceMs);
  };

  const persistImmediately = (): void => {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    if (events.length > 0) {
      writeToStorage(events);
    } else {
      clearStorage();
    }
  };

  return {
    enqueue: (event: AnalyticsSdkEvent) => {
      if (events.length >= maxSize) {
        events.shift();
      }
      events.push(event);
      schedulePersist();
    },

    enqueueMany: (batch: AnalyticsSdkEvent[], position: EnqueuePosition) => {
      if (position === 'front') {
        events.unshift(...batch);
        while (events.length > maxSize) {
          events.pop();
        }
      } else {
        events.push(...batch);
        while (events.length > maxSize) {
          events.shift();
        }
      }

      schedulePersist();
    },

    getBatch: (count: number): AnalyticsSdkEvent[] => events.slice(0, count),

    confirmSent: (count: number): void => {
      events.splice(0, count);
      persistImmediately();
    },

    size: () => events.length,

    persistImmediately,
  };
};
